import { Button, cardClass, Table } from '@ledgerloom/ui';
import { useId, useState } from 'react';
import { useApi, usePageTitle } from '../hooks';
import { ErrorBanner, forPeople, Loading, Time } from '../components/ui';
import { SettingsLayout } from './Admin';
import { hasSurface, useSession } from '../session';
import { OkrMap } from './Okrs';

// Settings, Business model (ADR 0025): the workspace's record kinds and their
// fields, the rules in force, its concepts, what reaches Recent activity on
// Home (ADR 0033), and the amendments behind them.
// Packs teach the workspace; what it changed it owns, and only the workspace
// designer drafts a change: a person describes it to the designer beside every
// settings page (SettingsLayout, ./Admin.tsx) and signs what it proposes. There is no field-by-field form. Patterns:
// data-display/table with expandable rows (record kinds), data-display/timeline
// (amendments), forms/textarea (the request), ai-intelligence/ai-loading-states.
// The OKR coach brings the map of objectives at the top (ADR 0037, ./Okrs.tsx).

interface Field { key: string; type: string; label: string | null; meaning: string | null; origin: string; locked: boolean; required: boolean; hidden: boolean; open: boolean; options: string[] | null; holding: number | null }
interface Kind { kind: string; pack: string; records: number; fields: Field[] }
interface Rule { id: string; pack: string; description: string; severity: string; reason: string; origin: string; disabled: boolean; disabled_reason: string | null }
interface Concept { key: string; label: string; definition: string; related: string[]; pack: string; origin: string }
interface Amendment { id: string; base_pack: string; base_version: string; kind: string; version: number; status: string; origin: string; signed_by_name: string | null; signed_at: string }
interface Topic { name: string; description: string; highlight: { line: string; many: string } | null; highlight_origin: string }
interface Model { packs: Array<{ name: string; title: string; version: string; amendable: boolean }>; record_kinds: Kind[]; rules: Rule[]; concepts: Concept[]; topics: Topic[]; amendments: Amendment[] }

const KIND_TITLE: Record<string, string> = { schema: 'Fields', rules: 'Rules', concepts: 'Concepts', defaults: 'Defaults', topics: 'Topics' };
const nice = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const originLabel = (o: string) => (o === 'pack' ? 'Pack' : o === 'designer' ? 'Designer' : o === 'pack_offer' ? 'Pack offer' : o.startsWith('agent:') ? `Agent ${o.slice(6)}` : o);

export function BusinessModel() {
  const { me } = useSession();
  usePageTitle('Business model');
  const model = useApi<Model>('/v1/business-model');
  const m = model.data;
  const packTitle = (name: string) => m?.packs.find((p) => p.name === name)?.title ?? name;
  return (
    <SettingsLayout title="Business model" lead="What your workspace is made of: its records and their fields, the rules in force, and its concepts. Packs teach it; what you change is yours, drafted by the workspace designer and signed by an admin."
      onDesignerChanged={() => void model.reload()}>
      <ErrorBanner error={model.error} />
      {hasSurface(me, 'okr_map') && <OkrMap />}
      {!m ? <Loading label="Loading the business model" /> : <>
        <RecordKinds kinds={m.record_kinds} packTitle={packTitle} />
        <Rules rules={m.rules} packTitle={packTitle} />
        <Concepts concepts={m.concepts} />
        <RecentActivity topics={m.topics} />
        <Changes amendments={m.amendments} packTitle={packTitle} />
      </>}
    </SettingsLayout>
  );
}

function RecordKinds({ kinds, packTitle }: { kinds: Kind[]; packTitle: (p: string) => string }) {
  return (
    <section className={cardClass} aria-labelledby="bm-kinds">
      <div className="card-head"><div><h2 id="bm-kinds">Records and fields</h2><div className="faint">A locked field is one Mazu's code reads, or one that guards money, identity, or consent: it can be relabelled, never hidden or made optional.</div></div></div>
      <Table label="Record kinds">
        <thead><tr><th scope="col">Record</th><th scope="col">From</th><th scope="col" className="num">Records</th><th scope="col" className="num">Fields</th><th scope="col"><span className="sr-only">Fields of this record</span></th></tr></thead>
        <tbody>{kinds.map((k) => <KindRow key={k.kind} k={k} packTitle={packTitle} />)}</tbody>
      </Table>
    </section>
  );
}

function KindRow({ k, packTitle }: { k: Kind; packTitle: (p: string) => string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const shown = k.fields.filter((f) => !f.hidden);
  return <>
    <tr>
      <th scope="row">{nice(k.kind)}</th>
      <td>{packTitle(k.pack)}</td>
      <td className="num">{k.records}</td>
      <td className="num">{shown.length}</td>
      <td><Button type="button" variant="ghost" size="sm" className="disclosure self-start pl-0" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'} the fields<span className="sr-only"> of {nice(k.kind)}</span></Button></td>
    </tr>
    <tr id={id} hidden={!open} className="bm-fields">
      <td colSpan={5}>{open && (
        <Table label={`Fields of ${nice(k.kind)}`} aria-label={`Fields of ${nice(k.kind)}`}>
          <thead><tr><th scope="col">Field</th><th scope="col">Label</th><th scope="col">Meaning</th><th scope="col">Type</th><th scope="col">Origin</th><th scope="col">Rules</th></tr></thead>
          <tbody>{k.fields.map((f) => (
            <tr key={f.key} className={f.hidden ? 'faint' : undefined}>
              <th scope="row"><code>{f.key}</code></th>
              <td>{f.label ?? <span className="faint">None</span>}</td>
              <td>{f.meaning ?? <span className="faint">None</span>}</td>
              <td>{f.type}{f.options ? `: ${f.options.join(', ')}` : ''}</td>
              <td>{originLabel(f.origin)}</td>
              <td>{[f.locked && 'Locked', f.required && 'Required', f.hidden && 'Hidden', f.holding !== null && `${f.holding} hold it`].filter(Boolean).join(', ') || <span className="faint">Open</span>}</td>
            </tr>
          ))}</tbody>
        </Table>
      )}</td>
    </tr>
  </>;
}

function Rules({ rules, packTitle }: { rules: Rule[]; packTitle: (p: string) => string }) {
  const off = rules.filter((r) => r.disabled);
  return (
    <section className={cardClass} aria-labelledby="bm-rules">
      <div className="card-head"><div><h2 id="bm-rules">Rules in force</h2><div className="faint">What the gate checks before anything is written. Hard rules refuse; control rules send it to a person.</div></div></div>
      <Table label="Rules">
        <thead><tr><th scope="col">Rule</th><th scope="col">What it checks</th><th scope="col">If broken</th><th scope="col">From</th><th scope="col">Origin</th></tr></thead>
        <tbody>{rules.filter((r) => !r.disabled).map((r) => (
          <tr key={r.id}><th scope="row"><code>{r.id}</code></th><td>{forPeople(r.description)}</td><td>{r.severity === 'hard' ? 'Refused' : 'Sent to a person'}</td><td>{packTitle(r.pack)}</td><td>{originLabel(r.origin)}</td></tr>
        ))}</tbody>
      </Table>
      {off.length > 0 && <div className="card-pad"><h3>Turned off</h3><ul>{off.map((r) => <li key={r.id}><code>{r.id}</code>: {r.disabled_reason} <span className="faint">({originLabel(r.origin)})</span></li>)}</ul></div>}
    </section>
  );
}

function Concepts({ concepts }: { concepts: Concept[] }) {
  return (
    <section className={cardClass} aria-labelledby="bm-concepts">
      <div className="card-head"><div><h2 id="bm-concepts">Concepts</h2><div className="faint">The words your agents read in every context pack, and the graph holds.</div></div></div>
      <dl className="card-pad bm-concepts">{concepts.map((c) => <div key={c.key}>
        <dt>{c.label} <span className="faint">{originLabel(c.origin)}</span></dt>
        <dd>{c.definition}{c.related.length ? <span className="faint"> Related: {c.related.join(', ')}.</span> : null}</dd>
      </div>)}</dl>
    </section>
  );
}

// What reaches Home's Recent activity (ADR 0033): each topic with a highlight, and how one event of it reads.
function RecentActivity({ topics }: { topics: Topic[] }) {
  const shown = topics.filter((t) => t.highlight);
  return (
    <section className={cardClass} aria-labelledby="bm-activity">
      <div className="card-head"><div><h2 id="bm-activity">Recent activity on Home</h2><div className="faint">What shows in Recent activity, and how it reads. Values in braces are filled in from the record. Ask the designer to add, reword, or take one off.</div></div></div>
      {!shown.length ? <p className="card-pad faint">Nothing shows in Recent activity.</p> : (
        <Table label="Recent activity"><caption className="sr-only">Recent activity</caption>
          <thead><tr><th scope="col">What happened</th><th scope="col">How it reads</th><th scope="col">Origin</th></tr></thead>
          <tbody>{shown.map((t) => (
            <tr key={t.name}><th scope="row">{t.description}</th><td><code>{t.highlight!.line}</code></td><td>{originLabel(t.highlight_origin)}</td></tr>
          ))}</tbody>
        </Table>
      )}
    </section>
  );
}

function Changes({ amendments, packTitle }: { amendments: Amendment[]; packTitle: (p: string) => string }) {
  return (
    <section className={`${cardClass} card-pad stack`} aria-labelledby="bm-changes" style={{ gap: 10 }}>
      <h2 id="bm-changes">Changes this workspace made</h2>
      {!amendments.length ? <p className="faint">None yet. Everything above is as the packs teach it.</p> : (
        <ol className="timeline" aria-label="Amendments, newest first">{amendments.map((a) => (
          <li key={a.id} className={a.status === 'active' ? 'ok' : undefined}>
            <strong>{KIND_TITLE[a.kind] ?? a.kind} of {packTitle(a.base_pack)}, version {a.version}</strong>
            {a.status === 'superseded' ? <span className="faint"> (superseded)</span> : null}
            <div className="faint">Drafted by {originLabel(a.origin)}, signed by {a.signed_by_name ?? 'an admin'} <Time iso={a.signed_at} />, over {packTitle(a.base_pack)} {a.base_version}</div>
          </li>
        ))}</ol>
      )}
    </section>
  );
}

